"use client";
import { useTranslations } from "@zapier/i18n";

import {
  useState,
  useDeferredValue,
  useCallback,
  useMemo,
  useLayoutEffect,
  useEffect,
  KeyboardEventHandler,
  useRef,
} from "react";

import {
  Button,
  Checkbox,
  ModalDialogBody,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogRoot,
  Text,
  Tooltip,
  TooltipFocusableTrigger,
  VisuallyHidden,
} from "@zapier/design-system";

import {
  FolderTreeItem,
  useAccountActionPermissions,
  useCreateFolder,
  useCreateSubfolder,
  useCurrentUser,
  useDeleteFolder,
  useFolderTree,
  useListFolderSubtreeMaxDepth,
  useSupportedAssets,
} from "@zapier/client";
import {
  AssetTypes,
  FolderTypeQueryState,
  isPersonalFolder,
  useNotification,
} from "@zapier/utils";
import { ButtonWrapper } from "../../../common/ButtonWrapper";
import { TruncatedRow } from "../../../common/TruncatedRow";
import {
  FolderTree,
  type DestinationDisableControls,
  type NewFolderControls,
  type SessionCreatedFolder,
  type SessionCreatedFolderControls,
} from "../../FolderTree/FolderTree";
import { collectSessionCreatedDescendants } from "../../FolderTree/pinSessionCreatedFolders";
import { FolderSharingAlert } from "../FolderSharingAlert";
import { NextGenZapSharingAlert } from "../NextGenZapSharingAlert";
import { FolderFilter } from "./FolderFilter";
import { MoveModalInnerProps } from "./types";
import { shouldHidePrivateFoldersForMove } from "./sourceFolderVisibility";
import {
  getCreateFolderDepthDisabledReason,
  isDestinationDepthAllowed,
  MAX_SUBFOLDER_DEPTH,
} from "../../../zsl/CopyToAnotherAccountModal/copyToAnotherAccountUtils";
import type { FolderTreeRowCounts } from "../../FolderTree/countFolderTreeRows";
import styles from "./MoveModal.module.css";

const EMPTY_SOURCE_FOLDER_IDS: string[] = [];
/** In-memory filter is instant; wait for typing to pause before announcing. */
const MOVE_FOLDER_SEARCH_ANNOUNCE_DEBOUNCE_MS = 600;
/** Brief defer after debounce so the last keystroke is not competing with status. */
const MOVE_FOLDER_SEARCH_ANNOUNCE_DEFER_MS = 150;

export function MoveModalInner({
  assetType,
  assetIds,
  sourceFolderIds,
  assetsAreShared,
  assetParentId,
  assetTitle = "",
  bodyAlert,
  disabledFolderIds,
  forceSubmitDisabled,
  headerOverride,
  isAssetOwnedByUser,
  nextGenZapCount = 0,
  onMove,
  onUnnest,
  setIsOpen,
  submitDisabledTooltip,
}: MoveModalInnerProps) {
  const t = useTranslations("move-modal-inner");
  const { createNotification } = useNotification();

  const { isMPFEnabled } = useSupportedAssets();
  const { user } = useCurrentUser();
  const [selectedParentFolder, setSelectedParentFolder] = useState<
    FolderTreeItem | undefined
  >();
  const [searchQuery, setSearchQuery] = useState<string>("");
  // Typing stays responsive while the expensive tree re-render lags a beat.
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const [filterOwnedByMe, setFilterOwnedByMe] = useState(false);
  const [selectionAnnouncement, setSelectionAnnouncement] = useState("");
  // Announcements alternate between two regions. Repeating a message in one
  // region is not re-read, but writing it to the empty sibling always is.
  const [searchStatusSlots, setSearchStatusSlots] = useState<[string, string]>([
    "",
    "",
  ]);
  const searchStatusSlotReference = useRef<0 | 1>(0);
  const [folderTreeRowCounts, setFolderTreeRowCounts] =
    useState<FolderTreeRowCounts>({
      searchQuery: "",
      matchCount: 0,
      shownCount: 0,
    });
  const [searchAnnouncementRequest, setSearchAnnouncementRequest] = useState(0);
  const searchQueryReference = useRef(searchQuery);
  const filteredFolderCountReference = useRef(0);
  const matchedFolderCountReference = useRef(0);
  const hasInitializedSearchAnnouncementReference = useRef(false);
  /** Set once a search is waiting for its result count to settle. */
  const isSearchAnnouncementPendingReference = useRef(false);
  /** Skips the announcement for a query reset the user did not ask for. */
  const suppressNextSearchAnnouncementReference = useRef(false);
  const previousSearchQueryReference = useRef(searchQuery);
  const wasFolderTreeLoadingReference = useRef(false);
  const searchDebounceTimeoutReference = useRef<
    ReturnType<typeof setTimeout> | undefined
  >(undefined);
  const searchAnnounceTimeoutReference = useRef<
    ReturnType<typeof setTimeout> | undefined
  >(undefined);
  const [pendingNewFolder, setPendingNewFolder] = useState<
    | {
        parentId: string | undefined;
        draftName?: string;
      }
    | undefined
  >();
  // CAA pattern: keep folders created in this modal session pinned at the
  // bottom of their parent (and deletable) even after folderTree refetch.
  const [sessionCreatedFolders, setSessionCreatedFolders] = useState<
    SessionCreatedFolder[]
  >([]);
  const isCreatingReference = useRef(false);
  // Set before focus leaves the new-folder input (e.g. Cancel mousedown) so
  // blur-to-commit cannot create a folder while the modal is closing.
  const suppressCreateCommitReference = useRef(false);
  const folderTreeScrollReference = useRef<HTMLDivElement>(null);
  // CAA keeps the create row in-place in a flat list with no focus restore.
  // Our nested tree + cache invalidate + selection focus still move scroll, so
  // hard-lock it from commit through post-settle refetch.
  const folderTreeScrollLockReference = useRef<{
    active: boolean;
    top: number;
    pinToBottom: boolean;
  }>({ active: false, top: 0, pinToBottom: false });

  const applyFolderTreeScrollLock = useCallback(() => {
    const element = folderTreeScrollReference.current;
    const lock = folderTreeScrollLockReference.current;
    if (!element || !lock.active) return;
    if (lock.pinToBottom) {
      element.scrollTop = element.scrollHeight - element.clientHeight;
      return;
    }
    element.scrollTop = lock.top;
  }, []);

  const lockFolderTreeScroll = useCallback(() => {
    const element = folderTreeScrollReference.current;
    if (!element) return;
    const maxScrollTop = element.scrollHeight - element.clientHeight;
    const pinToBottom = maxScrollTop - element.scrollTop <= 48;
    folderTreeScrollLockReference.current = {
      active: true,
      top: element.scrollTop,
      pinToBottom,
    };
    applyFolderTreeScrollLock();
  }, [applyFolderTreeScrollLock]);

  const unlockFolderTreeScroll = useCallback(() => {
    folderTreeScrollLockReference.current.active = false;
  }, []);

  useLayoutEffect(() => {
    applyFolderTreeScrollLock();
  });

  useEffect(() => {
    const element = folderTreeScrollReference.current;
    if (!element) return;
    const handleScroll = () => {
      if (!folderTreeScrollLockReference.current.active) return;
      applyFolderTreeScrollLock();
    };
    element.addEventListener("scroll", handleScroll);
    return () => element.removeEventListener("scroll", handleScroll);
  }, [applyFolderTreeScrollLock]);

  const { create: createFolder, isLoading: isCreatingFolder } =
    useCreateFolder();
  const { createSubfolder, isLoading: isCreatingSubfolder } =
    useCreateSubfolder();
  const { delete: deleteFolder, isLoading: isDeletingFolder } =
    useDeleteFolder();

  const { isMultiUserAccount, isSharingEnabled } =
    useAccountActionPermissions();
  const sourceFolderIdsForVisibility =
    sourceFolderIds ?? EMPTY_SOURCE_FOLDER_IDS;
  const hasVisibilityFolderSource = sourceFolderIdsForVisibility.length > 0;

  // Prefer explicit sourceFolderIds (bulk + single-folder move) so depth
  // gating works even when assetType is unset (BulkMoveModal).
  const folderIdsForSubtreeDepth = useMemo(() => {
    if (sourceFolderIds && sourceFolderIds.length > 0) {
      return [...new Set(sourceFolderIds.map(String).filter(Boolean))];
    }
    if (assetType === AssetTypes.Folder) {
      const ids = assetIds ?? [];
      return [...new Set(ids.map(String).filter(Boolean))];
    }
    return [];
  }, [assetType, sourceFolderIds, assetIds]);

  const hasFolderSource = folderIdsForSubtreeDepth.length > 0;
  const {
    maxDepth: sourceSubtreeDepth,
    isLoading: isSourceSubtreeDepthLoading,
    error: sourceSubtreeDepthError,
  } = useListFolderSubtreeMaxDepth(
    hasFolderSource ? folderIdsForSubtreeDepth : undefined,
  );
  const sourceSubtreeMaxDepth =
    sourceSubtreeDepth !== undefined && sourceSubtreeDepth >= 1
      ? sourceSubtreeDepth
      : undefined;
  // Fail closed until depth is known so folder moves cannot bypass the limit.
  const isSourceSubtreeDepthUnresolved =
    hasFolderSource &&
    (isSourceSubtreeDepthLoading ||
      !!sourceSubtreeDepthError ||
      sourceSubtreeMaxDepth === undefined);

  const { hash: folderTreeHash, isFoldersLoading } = useFolderTree(
    {
      disableOrdering: true,
      folderType: FolderTypeQueryState.All,
    },
    { enabled: true },
  );

  const isBulk = !!(assetIds && assetIds.length > 1);
  const isCreating = isCreatingFolder || isCreatingSubfolder;
  const isMutating = isCreating || isDeletingFolder;

  // Hold the lock until create finishes and invalidate/focus settle (CAA has
  // neither focus restore nor a shrinking scroll container).
  useEffect(() => {
    if (!folderTreeScrollLockReference.current.active) return;
    if (isCreating || isCreatingReference.current) {
      applyFolderTreeScrollLock();
      return;
    }
    applyFolderTreeScrollLock();
    const timeoutId = globalThis.setTimeout(() => {
      unlockFolderTreeScroll();
    }, 500);
    return () => globalThis.clearTimeout(timeoutId);
  }, [
    isCreating,
    pendingNewFolder,
    sessionCreatedFolders,
    selectedParentFolder?.id,
    applyFolderTreeScrollLock,
    unlockFolderTreeScroll,
  ]);

  searchQueryReference.current = searchQuery;

  const clearSearch = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      setSearchQuery("");
    },
    [],
  );

  const shouldHidePrivateFolders = useMemo(() => {
    if (!hasVisibilityFolderSource) {
      return false;
    }

    // Be conservative while source-folder visibility is still resolving to
    // avoid briefly showing private destinations for potentially shared moves.
    // Keep an already-chosen destination usable across post-create refetch
    // (otherwise Move stays disabled on a selected private folder).
    if (isFoldersLoading) {
      return !selectedParentFolder;
    }

    return shouldHidePrivateFoldersForMove(
      sourceFolderIdsForVisibility,
      folderTreeHash,
    );
  }, [
    folderTreeHash,
    hasVisibilityFolderSource,
    isFoldersLoading,
    selectedParentFolder,
    sourceFolderIdsForVisibility,
  ]);

  // Show all folders or just shared folders?
  const folderType: FolderTypeQueryState = useMemo(() => {
    // If type of move is folder and current folder is shared, only show shared folders
    // If type of move is Zap and current Zap is not owned by user, only show shared folders
    // If the source subtree includes a shared folder, only allow shared folders
    if (
      (assetType === AssetTypes.Folder && assetsAreShared) ||
      (assetType === AssetTypes.Zap && !isAssetOwnedByUser && !isMPFEnabled) ||
      shouldHidePrivateFolders
    ) {
      return FolderTypeQueryState.Shared;
    }

    // If type of move is Zap and the current Zap is owned by the user, allow user to move to any folder including personal
    return FolderTypeQueryState.All;
  }, [
    assetType,
    assetsAreShared,
    isAssetOwnedByUser,
    isMPFEnabled,
    shouldHidePrivateFolders,
  ]);

  const includeDefaultFolder =
    assetType === AssetTypes.Zap && isAssetOwnedByUser;

  const selectedOwnerForTree =
    filterOwnedByMe && typeof user?.id === "number" ? user.id : undefined;

  filteredFolderCountReference.current = folderTreeRowCounts.shownCount;
  matchedFolderCountReference.current = folderTreeRowCounts.matchCount;

  const cancelPendingSearchAnnouncementTimers = useCallback(() => {
    if (searchDebounceTimeoutReference.current !== undefined) {
      clearTimeout(searchDebounceTimeoutReference.current);
      searchDebounceTimeoutReference.current = undefined;
    }
    if (searchAnnounceTimeoutReference.current !== undefined) {
      clearTimeout(searchAnnounceTimeoutReference.current);
      searchAnnounceTimeoutReference.current = undefined;
    }
  }, []);

  const publishSearchMessage = useCallback((message: string) => {
    const slot = searchStatusSlotReference.current;
    searchStatusSlotReference.current = slot === 0 ? 1 : 0;
    setSearchStatusSlots(slot === 0 ? [message, ""] : ["", message]);
  }, []);

  const announceSearchResults = useCallback(() => {
    const query = searchQueryReference.current;
    const shown = filteredFolderCountReference.current;
    const matches = matchedFolderCountReference.current;
    if (!query) {
      publishSearchMessage(t("announcement.search-cleared", { count: shown }));
      return;
    }
    // Keyed on rows, so the message never contradicts the screen: ancestors
    // kept for structure can outnumber the matches, and a collapsed match can
    // leave a visible row that did not match at all.
    publishSearchMessage(
      shown > 0 && matches !== shown
        ? t("announcement.search-results-nested", { matches, shown })
        : t("announcement.search-results", { count: matches }),
    );
  }, [publishSearchMessage, t]);

  /** Marks a search as awaiting its result count. */
  const requestSearchAnnouncement = useCallback(() => {
    isSearchAnnouncementPendingReference.current = true;
    setSearchAnnouncementRequest((request) => request + 1);
  }, []);

  const scheduleSearchAnnouncementRequest = useCallback(() => {
    if (searchAnnounceTimeoutReference.current !== undefined) {
      clearTimeout(searchAnnounceTimeoutReference.current);
    }
    searchAnnounceTimeoutReference.current = setTimeout(() => {
      searchAnnounceTimeoutReference.current = undefined;
      requestSearchAnnouncement();
    }, MOVE_FOLDER_SEARCH_ANNOUNCE_DEFER_MS);
  }, [requestSearchAnnouncement]);

  useEffect(() => {
    return () => cancelPendingSearchAnnouncementTimers();
  }, [cancelPendingSearchAnnouncementTimers]);

  const handleSearchBlur = useCallback(() => {
    if (searchDebounceTimeoutReference.current === undefined) {
      return;
    }
    cancelPendingSearchAnnouncementTimers();
    scheduleSearchAnnouncementRequest();
  }, [
    cancelPendingSearchAnnouncementTimers,
    scheduleSearchAnnouncementRequest,
  ]);

  const handleSearchCommit = useCallback(() => {
    cancelPendingSearchAnnouncementTimers();
    requestSearchAnnouncement();
  }, [cancelPendingSearchAnnouncementTimers, requestSearchAnnouncement]);

  // Debounce on TYPING only. Result counts and loading flags must not restart
  // this clock, or a slow query keeps pushing its own announcement out.
  useEffect(() => {
    if (!hasInitializedSearchAnnouncementReference.current) {
      hasInitializedSearchAnnouncementReference.current = true;
      previousSearchQueryReference.current = searchQuery;
      return;
    }

    if (searchQuery !== previousSearchQueryReference.current) {
      previousSearchQueryReference.current = searchQuery;
      // A hold owed to the previous query must not swallow this one.
      wasFolderTreeLoadingReference.current = false;
      // Never clear the region here: removing its text mid-utterance truncates
      // the announcement a screen reader is still speaking. The next publish
      // writes to the empty sibling slot, which is change enough to be read.
      isSearchAnnouncementPendingReference.current = false;

      if (suppressNextSearchAnnouncementReference.current) {
        suppressNextSearchAnnouncementReference.current = false;
        cancelPendingSearchAnnouncementTimers();
        return;
      }
    }

    cancelPendingSearchAnnouncementTimers();
    searchDebounceTimeoutReference.current = setTimeout(() => {
      searchDebounceTimeoutReference.current = undefined;
      scheduleSearchAnnouncementRequest();
    }, MOVE_FOLDER_SEARCH_ANNOUNCE_DEBOUNCE_MS);

    return cancelPendingSearchAnnouncementTimers;
  }, [
    searchQuery,
    filterOwnedByMe,
    cancelPendingSearchAnnouncementTimers,
    scheduleSearchAnnouncementRequest,
  ]);

  // Announce once the results for that search have actually settled.
  useEffect(() => {
    if (!isSearchAnnouncementPendingReference.current) return;
    if (isFoldersLoading) {
      wasFolderTreeLoadingReference.current = true;
      return;
    }
    // The tree reports its counts one commit after the data lands, so the
    // commit that ends loading still carries the pre-load numbers. Skip it and
    // announce when the matching report arrives.
    if (wasFolderTreeLoadingReference.current) {
      wasFolderTreeLoadingReference.current = false;
      return;
    }
    if (folderTreeRowCounts.searchQuery !== searchQuery) return;
    isSearchAnnouncementPendingReference.current = false;
    announceSearchResults();
  }, [
    searchAnnouncementRequest,
    folderTreeRowCounts,
    searchQuery,
    isFoldersLoading,
    announceSearchResults,
  ]);

  const getFolderDepth = useCallback(
    (folderId: string): number => {
      let depth = 1;
      let currentId = folderId;
      while (folderTreeHash[currentId]?.parentId) {
        depth += 1;
        currentId = String(folderTreeHash[currentId].parentId);
      }
      return depth;
    },
    [folderTreeHash],
  );

  const effectiveSelectedParentFolder = useMemo(() => {
    if (!selectedParentFolder) {
      return;
    }
    if (!filterOwnedByMe || typeof user?.id !== "number") {
      return selectedParentFolder;
    }
    if (selectedParentFolder.ownerId !== user.id) {
      return;
    }
    return selectedParentFolder;
  }, [filterOwnedByMe, selectedParentFolder, user?.id]);

  const selectedDestinationDepth = useMemo(() => {
    if (!effectiveSelectedParentFolder) return;
    return getFolderDepth(String(effectiveSelectedParentFolder.id));
  }, [effectiveSelectedParentFolder, getFolderDepth]);

  const depthDisabledFolderIds = useMemo((): Set<string> | undefined => {
    if (!hasFolderSource || sourceSubtreeMaxDepth === undefined) return;
    const disabled = new Set<string>();
    for (const id of Object.keys(folderTreeHash)) {
      const destinationDepth = getFolderDepth(id);
      if (
        !isDestinationDepthAllowed({
          hasFolderSource,
          sourceSubtreeMaxDepth,
          destinationDepth,
          maxSubfolderDepth: MAX_SUBFOLDER_DEPTH,
        })
      ) {
        disabled.add(id);
      }
    }
    if (disabled.size > 0) return disabled;
  }, [hasFolderSource, sourceSubtreeMaxDepth, folderTreeHash, getFolderDepth]);

  // Mirror submit guards as visible tree disables: current parent (no-op),
  // source folders / their descendants (cycle / into-self), and non-owned
  // ancestors kept for hierarchy when "Owned by me" is on.
  const selectionDisabledFolderIds = useMemo(() => {
    const map = new Map<string, string>();
    const alreadyHereTooltip = t("tooltips.select-disabled-already-here");
    const intoSelfTooltip = t("tooltips.select-disabled-into-self");
    const notOwnedTooltip = t("tooltips.select-disabled-not-owned");

    if (assetParentId) {
      map.set(String(assetParentId), alreadyHereTooltip);
    }

    const blockedSourceIds = new Set<string>([
      ...(disabledFolderIds ?? []),
      ...folderIdsForSubtreeDepth,
    ]);

    for (const id of blockedSourceIds) {
      map.set(String(id), intoSelfTooltip);
    }

    // Walk each folder's parent chain so nested descendants of a non-root
    // source are blocked too (rootId alone only matches top-level roots).
    if (blockedSourceIds.size > 0 && folderTreeHash) {
      for (const id of Object.keys(folderTreeHash)) {
        if (map.has(id)) continue;
        const seen = new Set<string>();
        let currentId: string | undefined = id;
        while (currentId && folderTreeHash[currentId]) {
          if (seen.has(currentId)) break;
          seen.add(currentId);
          const parentId = folderTreeHash[currentId]?.parentId;
          // Loose equality so API null parentIds are treated as missing too.
          if (parentId == undefined) break;
          if (blockedSourceIds.has(String(parentId))) {
            map.set(id, intoSelfTooltip);
            break;
          }
          currentId = String(parentId);
        }
      }
    }

    if (filterOwnedByMe && typeof user?.id === "number" && folderTreeHash) {
      for (const [id, folder] of Object.entries(folderTreeHash)) {
        if (folder.ownerId !== user.id && !map.has(String(id))) {
          map.set(String(id), notOwnedTooltip);
        }
      }
    }

    return map.size > 0 ? map : undefined;
  }, [
    assetParentId,
    disabledFolderIds,
    filterOwnedByMe,
    folderIdsForSubtreeDepth,
    folderTreeHash,
    t,
    user?.id,
  ]);

  const createNewFolderDisabledReason = useMemo(() => {
    // Personal/Home is virtual — same-account createSubfolder under it is invalid.
    if (
      effectiveSelectedParentFolder &&
      isPersonalFolder(String(effectiveSelectedParentFolder.id))
    ) {
      return "personal-folder" as const;
    }
    if (isSourceSubtreeDepthUnresolved) {
      return "depth-unresolved" as const;
    }
    return getCreateFolderDepthDisabledReason({
      selectedParentDepth: selectedDestinationDepth,
      hasFolderSource,
      sourceSubtreeMaxDepth,
      maxSubfolderDepth: MAX_SUBFOLDER_DEPTH,
    });
  }, [
    effectiveSelectedParentFolder,
    selectedDestinationDepth,
    hasFolderSource,
    sourceSubtreeMaxDepth,
    isSourceSubtreeDepthUnresolved,
  ]);

  const handleFolderSelection = useCallback(
    (folder: FolderTreeItem) => {
      const isDeselecting =
        selectedParentFolder &&
        String(selectedParentFolder.id) === String(folder.id);
      if (isDeselecting) {
        setSelectedParentFolder(undefined);
        setSelectionAnnouncement(
          t("a11y.folder-deselected", { name: folder.name }),
        );
      } else {
        setSelectedParentFolder(folder);
        setSelectionAnnouncement(
          t("a11y.folder-selected", { name: folder.name }),
        );
      }
    },
    [selectedParentFolder, t],
  );

  const isSelectedDestinationDepthAllowed = useMemo(() => {
    if (!effectiveSelectedParentFolder || !selectedDestinationDepth) {
      return true;
    }
    return isDestinationDepthAllowed({
      hasFolderSource,
      sourceSubtreeMaxDepth,
      destinationDepth: selectedDestinationDepth,
      maxSubfolderDepth: MAX_SUBFOLDER_DEPTH,
    });
  }, [
    effectiveSelectedParentFolder,
    selectedDestinationDepth,
    hasFolderSource,
    sourceSubtreeMaxDepth,
  ]);

  const isSubmitDisabled = useMemo(() => {
    if (pendingNewFolder || isMutating) return true;
    // Fail closed while source subtree depth is unresolved so folder moves
    // cannot bypass the 5-level limit before the query settles / on error.
    if (isSourceSubtreeDepthUnresolved) return true;
    if (!effectiveSelectedParentFolder) return true;
    if (!isSelectedDestinationDepthAllowed) return true;

    const selectedId = String(effectiveSelectedParentFolder.id);
    const sourceAssetId =
      assetIds?.[0] == undefined ? undefined : String(assetIds[0]);
    const selectedRootId =
      effectiveSelectedParentFolder.rootId == undefined
        ? undefined
        : String(effectiveSelectedParentFolder.rootId);
    const selectedParentOfDestination =
      effectiveSelectedParentFolder.parentId == undefined
        ? undefined
        : String(effectiveSelectedParentFolder.parentId);

    // Block selecting any explicitly disabled folder (used by bulk move to
    // prevent moving a folder into itself or a sibling in the selection)
    if (disabledFolderIds?.includes(selectedId)) return true;
    if (depthDisabledFolderIds?.has(selectedId)) return true;
    if (selectionDisabledFolderIds?.has(selectedId)) return true;

    // Bulk-move descendant guard: block destinations whose rootId or parentId
    // matches a selected folder. Mirrors the single-move checks below but
    // works across multiple selected folders.
    if (
      disabledFolderIds?.some(
        (id) =>
          (selectedRootId != undefined && id === selectedRootId) ||
          (selectedParentOfDestination != undefined &&
            id === selectedParentOfDestination),
      )
    ) {
      return true;
    }

    // Shared-only mode guard: if source visibility rules require shared
    // destinations, block any private selection even if it was selected before
    // the folder tree finished resolving.
    if (shouldHidePrivateFolders && !effectiveSelectedParentFolder.isShared) {
      return true;
    }

    // Single-move guards: don't allow moving to the folder you're already in,
    // moving a folder into itself, or into its own descendant.
    // Compare only defined ids — `String(undefined) === String(undefined)`
    // would incorrectly disable Move for root destinations (no rootId/parentId).
    if (
      (assetType === "folder" &&
        sourceAssetId != undefined &&
        selectedId === sourceAssetId) ||
      (assetParentId != undefined && selectedId === String(assetParentId)) ||
      (sourceAssetId != undefined && selectedRootId === sourceAssetId) ||
      (sourceAssetId != undefined &&
        selectedParentOfDestination === sourceAssetId)
    ) {
      return true;
    }

    return false;
  }, [
    pendingNewFolder,
    isMutating,
    isSourceSubtreeDepthUnresolved,
    assetType,
    assetIds,
    assetParentId,
    disabledFolderIds,
    depthDisabledFolderIds,
    selectionDisabledFolderIds,
    effectiveSelectedParentFolder,
    shouldHidePrivateFolders,
    isSelectedDestinationDepthAllowed,
  ]);

  const onFormSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      // The dialog never posts; letting the default through resets the fields.
      event.preventDefault();
      if (isSubmitDisabled || !effectiveSelectedParentFolder) return;

      onMove({
        selectedFolderId: effectiveSelectedParentFolder.id,
        selectedFolderName: effectiveSelectedParentFolder.name,
        selectedFolderRootId: effectiveSelectedParentFolder.rootId || undefined,
        selectedFolderIsShared: effectiveSelectedParentFolder.isShared,
      });
    },
    [onMove, effectiveSelectedParentFolder, isSubmitDisabled],
  );

  const onFormKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (pendingNewFolder) return;
      // if the enter key is being pressed submit the form
      if (isSubmitDisabled || !effectiveSelectedParentFolder) {
        return;
      }
      return (
        event.key === "Enter" &&
        onMove({
          selectedFolderId: effectiveSelectedParentFolder.id,
          selectedFolderName: effectiveSelectedParentFolder.name,
          selectedFolderRootId:
            effectiveSelectedParentFolder.rootId || undefined,
          selectedFolderIsShared: effectiveSelectedParentFolder.isShared,
        })
      );
    },
    [pendingNewFolder, effectiveSelectedParentFolder, isSubmitDisabled, onMove],
  );

  const onSubmit = useCallback(() => {
    if (isSubmitDisabled || !effectiveSelectedParentFolder) return;

    onMove({
      selectedFolderId: effectiveSelectedParentFolder.id,
      selectedFolderName: effectiveSelectedParentFolder.name,
      selectedFolderRootId: effectiveSelectedParentFolder.rootId || undefined,
      selectedFolderIsShared: effectiveSelectedParentFolder.isShared,
    });
  }, [onMove, effectiveSelectedParentFolder, isSubmitDisabled]);

  const handleStartNewFolder = useCallback(() => {
    if (pendingNewFolder || isCreatingReference.current) return;
    if (createNewFolderDisabledReason) return;
    // Opening the name input must never be announced over. Drop any pending
    // announcement outright, including a blur flush armed by an empty query,
    // which would otherwise land while the input has just taken focus.
    cancelPendingSearchAnnouncementTimers();
    isSearchAnnouncementPendingReference.current = false;
    if (searchQuery) {
      suppressNextSearchAnnouncementReference.current = true;
      setSearchQuery("");
    }
    suppressCreateCommitReference.current = false;
    setPendingNewFolder({
      parentId: effectiveSelectedParentFolder
        ? String(effectiveSelectedParentFolder.id)
        : undefined,
    });
  }, [
    pendingNewFolder,
    createNewFolderDisabledReason,
    effectiveSelectedParentFolder,
    searchQuery,
    cancelPendingSearchAnnouncementTimers,
  ]);

  const handleCancelNewFolder = useCallback(() => {
    suppressCreateCommitReference.current = true;
    setPendingNewFolder(undefined);
  }, []);

  const handleCloseModal = useCallback(() => {
    suppressCreateCommitReference.current = true;
    setPendingNewFolder(undefined);
    setIsOpen(false);
  }, [setIsOpen]);

  const handleCommitNewFolder = useCallback(
    (name: string) => {
      if (suppressCreateCommitReference.current) return;
      if (!pendingNewFolder || isCreatingReference.current) return;
      isCreatingReference.current = true;
      // CAA: keep the pending input mounted; lock scroll so invalidate/focus
      // cannot jump the list away from the create row.
      lockFolderTreeScroll();

      const parentId = pendingNewFolder.parentId;
      // Subfolders inherit the parent's sharing; root creates follow folderType.
      const parentIsShared = parentId
        ? Boolean(
            (effectiveSelectedParentFolder &&
              String(effectiveSelectedParentFolder.id) === parentId &&
              effectiveSelectedParentFolder.isShared) ||
            folderTreeHash[parentId]?.isShared,
          )
        : undefined;
      const isShared =
        parentId === undefined
          ? folderType === FolderTypeQueryState.Shared
          : !!parentIsShared;

      const notifyCreateFailed = () => {
        createNotification({
          message: t("errors.create-failed"),
          type: "error",
        });
      };

      const onCreateError = () => {
        notifyCreateFailed();
        setPendingNewFolder((previous) =>
          previous
            ? {
                parentId: previous.parentId,
                draftName: name,
              }
            : previous,
        );
      };

      const selectCreatedFolder = (created: {
        id: string;
        name: string;
        isShared: boolean;
        accountId: number;
        ownerId?: number | null;
        parentId?: string | null;
        rootId?: string | null;
      }) => {
        const sessionFolder: SessionCreatedFolder = {
          id: String(created.id),
          name: created.name,
          parentId:
            created.parentId == undefined
              ? undefined
              : String(created.parentId),
          isShared: created.isShared,
          accountId: created.accountId,
          ownerId: created.ownerId ?? user?.id,
          rootId:
            created.rootId == undefined ? undefined : String(created.rootId),
        };
        // Move focus out of the input before it unmounts so tree keyboard-nav
        // does not focus the new row and scrollIntoView it (CAA never does this).
        folderTreeScrollReference.current?.focus({ preventScroll: true });
        setSessionCreatedFolders((previous) => {
          if (previous.some((folder) => folder.id === sessionFolder.id)) {
            return previous;
          }
          return [...previous, sessionFolder];
        });
        setSelectedParentFolder({
          id: sessionFolder.id,
          name: sessionFolder.name,
          isShared: sessionFolder.isShared,
          accountId: sessionFolder.accountId,
          // Owned-by-me filter clears selection when ownerId is missing.
          ownerId: sessionFolder.ownerId,
          parentId: sessionFolder.parentId,
          rootId: sessionFolder.rootId,
          children: [],
        } as FolderTreeItem);
        setPendingNewFolder(undefined);
        applyFolderTreeScrollLock();
      };

      if (parentId) {
        createSubfolder(
          { name, isShared, parentId },
          {
            onSuccess: (data) => {
              const created = data[0];
              if (created) {
                selectCreatedFolder(created);
                return;
              }
              onCreateError();
            },
            onError: onCreateError,
            onSettled: () => {
              isCreatingReference.current = false;
            },
          },
        );
        return;
      }

      createFolder(
        { name, isShared },
        {
          onSuccess: (data) => {
            selectCreatedFolder(data);
          },
          onError: onCreateError,
          onSettled: () => {
            isCreatingReference.current = false;
          },
        },
      );
    },
    [
      pendingNewFolder,
      folderType,
      folderTreeHash,
      effectiveSelectedParentFolder,
      createSubfolder,
      createFolder,
      user?.id,
      createNotification,
      t,
      lockFolderTreeScroll,
      applyFolderTreeScrollLock,
    ],
  );

  const handleDeleteSessionFolder = useCallback(
    (folderId: string) => {
      const descendantsDeepestFirst = collectSessionCreatedDescendants(
        folderId,
        sessionCreatedFolders,
      );
      // Cascade deepest-first (CAA), then the clicked folder last.
      const ordered = [...descendantsDeepestFirst, folderId];

      const deleteCascade = (index: number) => {
        if (index >= ordered.length) {
          const removed = new Set(ordered);
          setSessionCreatedFolders((previous) =>
            previous.filter((folder) => !removed.has(String(folder.id))),
          );
          setSelectedParentFolder((previous) =>
            previous && removed.has(String(previous.id)) ? undefined : previous,
          );
          return;
        }

        deleteFolder(
          { folderId: ordered[index] },
          {
            onSuccess: () => deleteCascade(index + 1),
            onError: () => {
              createNotification({
                message: t("errors.delete-failed"),
                type: "error",
              });
            },
          },
        );
      };

      deleteCascade(0);
    },
    [sessionCreatedFolders, deleteFolder, createNotification, t],
  );

  const canWarnAboutSharedDestination =
    !!effectiveSelectedParentFolder?.isShared && !isSubmitDisabled;
  const shouldShowFolderSharingAlert =
    (assetType === AssetTypes.Folder || hasVisibilityFolderSource) &&
    canWarnAboutSharedDestination;
  const shouldShowNextGenZapSharingAlert =
    nextGenZapCount > 0 && canWarnAboutSharedDestination;
  const sourceFolderCount = sourceFolderIdsForVisibility.length;
  const folderSharingAlertAssetTitle =
    assetTitle?.trim() ||
    (sourceFolderCount === 1
      ? folderTreeHash?.[sourceFolderIdsForVisibility[0]]?.name || ""
      : "");
  const privateDestinationHiddenDisclaimer = t(
    "messages.private-destination-hidden-note",
  );

  const isCreateNewFolderDisabled =
    !!pendingNewFolder ||
    isMutating ||
    createNewFolderDisabledReason !== undefined;

  const createNewFolderTooltip = (() => {
    if (!createNewFolderDisabledReason) return;
    if (createNewFolderDisabledReason === "personal-folder") {
      return t("tooltips.create-disabled-personal");
    }
    if (createNewFolderDisabledReason === "depth-unresolved") {
      return t("tooltips.create-disabled-depth-loading");
    }
    if (createNewFolderDisabledReason === "copy-projection-depth") {
      return t("tooltips.create-disabled-move-depth", {
        maxDepth: MAX_SUBFOLDER_DEPTH,
      });
    }
    return t("tooltips.create-disabled-max-depth", {
      maxDepth: MAX_SUBFOLDER_DEPTH,
    });
  })();

  const newFolderInputAriaLabel = t("new-folder-input-label");

  const depthDisabledTooltip = t("tooltips.select-disabled-depth", {
    maxDepth: MAX_SUBFOLDER_DEPTH,
  });

  const newFolder = useMemo<NewFolderControls>(
    () => ({
      pending: pendingNewFolder,
      onCommit: handleCommitNewFolder,
      onCancel: handleCancelNewFolder,
      placeholder: t("new-folder-placeholder"),
      inputAriaLabel: newFolderInputAriaLabel,
      isCreating,
    }),
    [
      pendingNewFolder,
      handleCommitNewFolder,
      handleCancelNewFolder,
      t,
      newFolderInputAriaLabel,
      isCreating,
    ],
  );

  const destinationDisables = useMemo<DestinationDisableControls>(
    () => ({
      depthDisabledFolderIds,
      depthDisabledTooltip,
      selectionDisabledFolderIds,
    }),
    [depthDisabledFolderIds, depthDisabledTooltip, selectionDisabledFolderIds],
  );

  const deleteSessionFolderAriaLabel = t("delete-new-folder-label");
  const sessionCreatedFolderControls = useMemo<SessionCreatedFolderControls>(
    () => ({
      folders: sessionCreatedFolders,
      onDelete: handleDeleteSessionFolder,
      deleteAriaLabel: deleteSessionFolderAriaLabel,
      isDeleting: isDeletingFolder,
    }),
    [
      sessionCreatedFolders,
      handleDeleteSessionFolder,
      deleteSessionFolderAriaLabel,
      isDeletingFolder,
    ],
  );

  return (
    <ModalDialogRoot
      isOpen={true}
      onOpenChange={(open) => {
        if (!open) {
          suppressCreateCommitReference.current = true;
          setPendingNewFolder(undefined);
        }
        setIsOpen(open);
      }}
    >
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- the form deliberately stops click/keydown propagation to keep the modal open and submit on Enter */}
      <form
        className={styles.form}
        onSubmit={onFormSubmit}
        onClick={(event) => event.stopPropagation()} // prevent the modal from closing when clicking inside the form
        onKeyDown={
          onFormKeyDown as unknown as KeyboardEventHandler<HTMLFormElement>
        }
        onMouseDownCapture={(event) => {
          // Runs before the new-folder input blurs when Cancel is pressed.
          const target = event.target;
          if (
            pendingNewFolder &&
            target instanceof Element &&
            target.closest("[data-move-modal-cancel]")
          ) {
            suppressCreateCommitReference.current = true;
          }
        }}
      >
        <ModalDialogHeader textAlign="start">
          {headerOverride || (
            <TruncatedRow.Root>
              {isBulk
                ? t("header.move-title-bulk", { title: assetTitle || "" })
                : t.rich("header.move-title", {
                    title: assetTitle || "",
                    truncated: (chunks) => (
                      <>
                        <span aria-label="quote">&ldquo;</span>
                        <TruncatedRow.Truncated>
                          {chunks}
                        </TruncatedRow.Truncated>
                        <span aria-label="end quote">&rdquo;</span>
                      </>
                    ),
                  })}
            </TruncatedRow.Root>
          )}
        </ModalDialogHeader>

        <ModalDialogBody textAlign="start">
          {bodyAlert}
          {isMultiUserAccount && (
            <Text tag="p">{t("messages.folder-permissions-warning")}</Text>
          )}
          <div className={styles.folderFilterRow}>
            <FolderFilter
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              clearSearch={clearSearch}
              onSearchBlur={handleSearchBlur}
              onSearchCommit={handleSearchCommit}
            />
            {typeof user?.id === "number" ? (
              <Checkbox
                isSelected={filterOwnedByMe}
                value="owned-by-me"
                name="move-modal-owned-by-me"
                onChange={() => setFilterOwnedByMe((previous) => !previous)}
              >
                {t("buttons.owned-by-me")}
              </Checkbox>
            ) : undefined}
          </div>
          <div className={styles.folderTreeContainer}>
            <div
              ref={folderTreeScrollReference}
              className={styles.folderTreeWrapper}
              // Focus target so create-input unmount does not land focus on a
              // treeitem (which scrolls the list).
              tabIndex={-1}
            >
              {/* 
          - For moving personal folders, we show the user personal folders (without Home) AND shared folders.
          - For moving shared folders, we show the user only shared folders.
          - For moving Zaps that are owned by user, we show the user all folders (including Home)
          - For moving Zaps that aren't owned by user, we show the user only shared folders. 
          - For moving other assets, we show the user all folders, but no virtual folders
          */}
              <FolderTree
                onItemClick={handleFolderSelection}
                disableOrdering={true}
                folderType={folderType}
                includeDefaultFolder={includeDefaultFolder}
                searchQuery={deferredSearchQuery}
                selectedFolderId={effectiveSelectedParentFolder?.id}
                selectedOwner={selectedOwnerForTree}
                disableSharedFolders={!isSharingEnabled}
                newFolder={newFolder}
                destinationDisables={destinationDisables}
                sessionCreatedFolders={sessionCreatedFolderControls}
                onResultsChange={setFolderTreeRowCounts}
              />
            </div>
            {shouldShowFolderSharingAlert ? (
              <div className={styles.folderSharingAlert}>
                <FolderSharingAlert
                  assetTitle={folderSharingAlertAssetTitle}
                  parentFolderName={effectiveSelectedParentFolder?.name}
                  sourceFolderCount={sourceFolderCount}
                />
              </div>
            ) : undefined}
            {shouldShowNextGenZapSharingAlert ? (
              <div className={styles.folderSharingAlert}>
                <NextGenZapSharingAlert
                  parentFolderName={effectiveSelectedParentFolder?.name ?? ""}
                />
              </div>
            ) : undefined}
            {shouldHidePrivateFolders && (
              <Text
                type="SmallPrint1"
                color="TextWeaker"
                tag="p"
                className={styles.destinationDisclaimer}
              >
                {privateDestinationHiddenDisclaimer}
              </Text>
            )}
          </div>
          {/* Live regions at the end of the dialog body so status updates do not
              interleave with search field focus-out speech in the tab order. */}
          <VisuallyHidden>
            <div role="status" aria-live="polite" aria-atomic="true">
              {selectionAnnouncement}
            </div>
          </VisuallyHidden>
          {searchStatusSlots.map((message, index) => (
            <VisuallyHidden key={index}>
              <div role="status" aria-live="polite" aria-atomic="true">
                {message}
              </div>
            </VisuallyHidden>
          ))}
        </ModalDialogBody>
        <ModalDialogFooter>
          <div className={styles.footer}>
            {(() => {
              const createButton = (
                <button
                  type="button"
                  className={styles.createButton}
                  disabled={isCreateNewFolderDisabled}
                  onClick={handleStartNewFolder}
                >
                  {t("buttons.create-new-folder")}
                </button>
              );
              if (!createNewFolderTooltip) return createButton;
              return (
                <Tooltip allowMultiline content={createNewFolderTooltip}>
                  <ButtonWrapper isDisabled={isCreateNewFolderDisabled}>
                    {createButton}
                  </ButtonWrapper>
                </Tooltip>
              );
            })()}
            <div className={styles.ctaButtons}>
              <Button
                variant="secondary"
                data-move-modal-cancel=""
                onPress={handleCloseModal}
              >
                {t("buttons.cancel")}
              </Button>
              {hasFolderSource && onUnnest && (
                <Button
                  isDisabled={
                    !assetParentId || isMutating || !!pendingNewFolder
                  }
                  type="button"
                  onPress={onUnnest}
                >
                  {t("buttons.move-to-top-level")}
                </Button>
              )}
              {forceSubmitDisabled && submitDisabledTooltip ? (
                <Tooltip
                  allowMultiline={true}
                  content={submitDisabledTooltip}
                  placement="top"
                >
                  <TooltipFocusableTrigger>
                    <Button type="submit" isDisabled={true}>
                      {t("buttons.move-to-folder")}
                    </Button>
                  </TooltipFocusableTrigger>
                </Tooltip>
              ) : (
                <Button
                  type="submit"
                  isDisabled={isSubmitDisabled || forceSubmitDisabled}
                  onPress={onSubmit}
                >
                  {t("buttons.move-to-folder")}
                </Button>
              )}
            </div>
          </div>
        </ModalDialogFooter>
      </form>
    </ModalDialogRoot>
  );
}
